import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const AvatarUploader = ({ botAvatar, userAvatar, onBotAvatarChange, onUserAvatarChange }) => {
  const [dragOver, setDragOver] = useState({ bot: false, user: false });

  const handleFileUpload = (file, type) => {
    if (file && file?.type?.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (type === 'bot') {
          onBotAvatarChange(e?.target?.result);
        } else {
          onUserAvatarChange(e?.target?.result);
        }
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleDrop = (e, type) => {
    e?.preventDefault();
    setDragOver({ ...dragOver, [type]: false });
    const file = e?.dataTransfer?.files?.[0];
    handleFileUpload(file, type);
  };

  const handleDragOver = (e, type) => {
    e?.preventDefault();
    setDragOver({ ...dragOver, [type]: true });
  };

  const handleDragLeave = (e, type) => {
    e?.preventDefault();
    setDragOver({ ...dragOver, [type]: false });
  };

  const AvatarUploadArea = ({ avatar, type, label, description }) => (
    <div className="space-y-3">
      <div>
        <label className="text-sm font-medium text-foreground block mb-1">{label}</label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-6 transition-all duration-200
          ${dragOver?.[type] 
            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
          }
        `}
        onDrop={(e) => handleDrop(e, type)}
        onDragOver={(e) => handleDragOver(e, type)}
        onDragLeave={(e) => handleDragLeave(e, type)}
      >
        <div className="flex flex-col items-center space-y-4">
          {avatar ? (
            <div className="relative">
              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-border">
                <Image
                  src={avatar}
                  alt={`${type} avatar`}
                  className="w-full h-full object-cover"
                />
              </div>
              <button
                onClick={() => type === 'bot' ? onBotAvatarChange('') : onUserAvatarChange('')}
                className="absolute -top-1 -right-1 w-6 h-6 bg-error text-error-foreground rounded-full flex items-center justify-center hover:bg-error/80 transition-colors"
              >
                <Icon name="X" size={12} />
              </button>
            </div>
          ) : (
            <div className="w-16 h-16 rounded-full bg-muted border-2 border-border flex items-center justify-center">
              <Icon name="User" size={24} className="text-muted-foreground" />
            </div>
          )}
          
          <div className="text-center">
            <p className="text-sm font-medium text-foreground mb-1">
              {avatar ? 'Avatar ändern' : 'Avatar hochladen'}
            </p>
            <p className="text-xs text-muted-foreground">
              PNG, JPG bis zu 2MB
            </p>
          </div>
          
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileUpload(e?.target?.files?.[0], type)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
      </div>
      
      <div className="flex space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => document.querySelector(`input[type="file"]`)?.click()}
          iconName="Upload"
          iconPosition="left"
          className="flex-1"
        >
          Datei auswählen
        </Button>
        {avatar && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => type === 'bot' ? onBotAvatarChange('') : onUserAvatarChange('')}
            iconName="Trash2"
            className="text-error hover:text-error"
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      <h3 className="text-lg font-semibold text-foreground">Avatar-Einstellungen</h3>
      
      <div className="grid md:grid-cols-2 gap-6">
        <AvatarUploadArea
          avatar={botAvatar}
          type="bot"
          label="Bot-Avatar"
          description="Das Profilbild für den Chatbot"
        />
        
        <AvatarUploadArea
          avatar={userAvatar}
          type="user"
          label="Benutzer-Avatar"
          description="Standard-Avatar für Benutzer"
        />
      </div>
      
      {/* Preview */}
      <div className="p-4 bg-muted rounded-lg">
        <p className="text-xs font-medium text-muted-foreground mb-3">Chat-Vorschau</p>
        <div className="space-y-3">
          {/* Bot Message */}
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 rounded-full overflow-hidden border border-border flex-shrink-0">
              {botAvatar ? (
                <Image src={botAvatar} alt="Bot" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-primary flex items-center justify-center">
                  <Icon name="Bot" size={16} color="white" />
                </div>
              )}
            </div>
            <div className="bg-card border border-border rounded-lg px-3 py-2 max-w-xs">
              <p className="text-sm text-foreground">Hallo! Wie kann ich Ihnen helfen?</p>
            </div>
          </div>
          
          {/* User Message */}
          <div className="flex items-start space-x-3 justify-end">
            <div className="bg-primary text-primary-foreground rounded-lg px-3 py-2 max-w-xs">
              <p className="text-sm">Ich habe eine Frage zu Ihrem Service.</p>
            </div>
            <div className="w-8 h-8 rounded-full overflow-hidden border border-border flex-shrink-0">
              {userAvatar ? (
                <Image src={userAvatar} alt="User" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <Icon name="User" size={16} className="text-muted-foreground" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvatarUploader;